import type {DiffSide, StaticDiffLine, ThreadSubject} from '@github-ui/diff-lines/conversation-types'
import type {
  DiffLine,
  PositionedThread,
  ThreadPreviewPositioningResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'
import {ThreadPreviewPositioningResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'

import {
  legacyToProtoThreadPreviewPositioning,
  normalizeThreadPreviewPositioning,
} from './thread-preview-positioning-proto'

/**
 * Legacy (flag-off) response shape for the `thread_preview_positioning` endpoint: a top-level JSON
 * object keyed by review-thread id. TEMPORARY — used only by the legacy→proto shim and tests until
 * the `thread_preview_positioning_proto` flag is fully rolled out. Consumers read the canonical
 * proto `ThreadPreviewPositioningResponse` instead.
 */
export type LegacyThreadPreviewPositioning = {
  [threadId: number]: {
    diffLines: StaticDiffLine[]
    endDiffSide?: DiffSide
    endLine?: number | null
    isOutdated?: boolean
    path: string
    startDiffSide?: DiffSide
    startLine?: number | null
    originalDiffPathUri?: string | null
  }
}

/** Converts a proto `DiffLine` into the diff-lines domain `StaticDiffLine`. */
function protoDiffLineToStaticDiffLine(line: DiffLine): StaticDiffLine {
  return {
    html: line.html ?? '',
    left: line.left ?? null,
    right: line.right ?? null,
    text: line.text ?? '',
    type: line.type as StaticDiffLine['type'],
  }
}

/**
 * Converts a proto `PositionedThread` into the diff-lines domain `ThreadSubject`. This is a domain
 * translation (not a legacy shim) and remains after the proto flag is fully rolled out.
 */
export function positionedThreadToSubject(thread: PositionedThread): ThreadSubject {
  return {
    diffLines: thread.diffLines.map(protoDiffLineToStaticDiffLine),
    endDiffSide: thread.endDiffSide as DiffSide | undefined,
    endLine: thread.endLine ?? null,
    startDiffSide: thread.startDiffSide as DiffSide | undefined,
    startLine: thread.startLine ?? null,
    isOutdated: thread.isOutdated,
  }
}

export function useThreadPreviewPositioning(threadIds: number[]) {
  const pathname = usePageDataUrl(PageData.threadPreviewPositioning)
  const threadIdsParam = threadIds.join(',')
  const threadPreviewPositioningUrl = `${pathname}?thread_ids=${threadIdsParam}`

  return useQuery<ThreadPreviewPositioningResponse>({
    queryKey: [PageData.threadPreviewPositioning, threadPreviewPositioningUrl],
    queryFn: async () => {
      if (!threadIds.length) {
        return legacyToProtoThreadPreviewPositioning({})
      }

      // `queryFnFetch` decodes proto-mode responses (flag on) via the schema; the legacy (flag off)
      // JSON passes through untouched and is converted up to the proto shape by `normalize…`.
      const result = await queryFnFetch<typeof ThreadPreviewPositioningResponseSchema, LegacyThreadPreviewPositioning>(
        {
          queryDeps: {pathname, searchParams: [['thread_ids', threadIdsParam]]},
          routeId: 'pull_request',
          queryName: 'threadPreviewPositioning',
        },
        {schema: ThreadPreviewPositioningResponseSchema},
      )
      return normalizeThreadPreviewPositioning(result)
    },
    staleTime: Infinity,
  })
}
